/**
 * 对话消息切片 · AI 智能分类（v4 · 对齐 demo SSE 协议）
 *
 * 模型变化（v3 → v4）
 * ───────────────────────────────────────────────────────────
 * v3 用 thinking / plan-preview / exec-progress 三种 AI 气泡，每种维护各自状态。
 * v4 改为单一 AssistantMessage：
 *   · streaming 期间累加 SSE delta 文本到 .text
 *   · done 事件落 .payload（含 action / card / bottomOptions / toolCalls）
 *   · error 事件落 .errorMessage
 *
 * 本切片职责
 * ───────────────────────────────────────────────────────────
 *   · EntityAdapter 扁平存储 TextMessage | AssistantMessage 联合
 *   · streaming 三阶段事件入口（appended / deltaAppended / done / errored）
 *   · sessionSlice.reset 时联动清空
 */

import {
  createSlice,
  createEntityAdapter,
  PayloadAction
} from '@reduxjs/toolkit'
import type {
  AssistantAction,
  AssistantCard,
  AssistantMessage,
  AssistantPayload,
  ChatMessage,
  LayerSummary,
  PlanFolder,
  ToolCallRecord
} from '../../types/ai-classify'
import { conversationCleared, teamSwitched } from './sessionSlice'

export const adapter = createEntityAdapter<ChatMessage>({
  selectId: (m) => m.id
})

const messagesSlice = createSlice({
  name: 'aiClassify/messages',
  initialState: adapter.getInitialState(),
  reducers: {
    /** 追加一条消息 · TextMessage（user）或 AssistantMessage（streaming 起点） */
    messageAppended: adapter.addOne,

    /**
     * SSE done 事件 · 落 payload 并把 status 转 done
     *
     * ⚠️ payload 不能整段替换，需要做字段级 merge:
     *
     *   · toolCalls: streaming 期间由 assistantToolStarted / assistantToolEnded
     *     累积；done 事件不带, 整段覆盖会清空思维链 → ToolCallsSummary 看上去
     *     "思考完成后隐藏了"。
     *   · card: v6 纯 dimension_op 路径下, 方案卡由 assistantPlanSnapshotted 在
     *     streaming 期间写入 (folder_plan kind); done 事件本身的 e.card 在这种
     *     场景下是 undefined。如果整段覆盖 → card 被 undefined 写没了, 卡片消失。
     *     v5 / v6 ask/next_level 路径下 done.card 是真值 (final_proposal/ask 等),
     *     此时应该覆盖之前的快照。`??` fallback 同时满足两种语义。
     *
     * 解决: merge — 优先用 done 自带的字段，缺失的字段从原 payload 兜底。
     */
    assistantDone(
      state,
      action: PayloadAction<{
        messageId: string
        finalText?: string
        payload: AssistantPayload
        /** 后端 ai_chat_message.id · 反馈接口必传 */
        backendMessageId?: number
      }>
    ) {
      const { messageId, finalText, payload, backendMessageId } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      if (typeof finalText === 'string') msg.text = finalText
      const prev = msg.payload
      msg.payload = {
        ...prev,
        ...payload,
        // 保留 streaming 累积的 toolCalls / card：done 不带或 done 带空时整段覆盖会丢
        toolCalls: payload.toolCalls ?? prev?.toolCalls,
        card: payload.card ?? prev?.card
      }
      msg.status = 'done'
      if (backendMessageId != null) msg.backendMessageId = backendMessageId
    },

    /**
     * 设置消息反馈 · 点赞 (1) / 点踩 (2) · 撤销时传 0
     *  · 仅 assistant 消息支持
     *  · API 调用由 useAIClassifySession / 组件层负责，本 reducer 只更状态
     */
    messageFeedbackSet(
      state,
      action: PayloadAction<{
        messageId: string
        feedback: 0 | 1 | 2
      }>
    ) {
      const { messageId, feedback } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      msg.feedback = feedback
    },

    /**
     * tool_start 事件 · streaming 期间实时把工具占位 push 到消息的 toolCalls
     *
     * 用户能看到 "正在搜索水印「项目」相关照片..." 之类的工具进行中提示。
     * tool_end 时同 id 会被 update 成完成态（resultSummary + durationMs）。
     */
    assistantToolStarted(
      state,
      action: PayloadAction<{ messageId: string; record: ToolCallRecord }>
    ) {
      const { messageId, record } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      if (msg.status !== 'streaming') return
      if (!msg.payload) {
        // streaming 期间还没 done，先撑一个空 payload 让 toolCalls 有处可挂
        msg.payload = { action: 'final_proposal', toolCalls: [] }
      }
      if (!msg.payload.toolCalls) msg.payload.toolCalls = []
      msg.payload.toolCalls.push(record)
    },

    /**
     * tool_end 事件 · 按 id 找到 streaming 期间 push 的占位记录并更新完成态
     *
     * 没找到（start 漏了）就直接 push 一条；保证最终列表完整。
     */
    assistantToolEnded(
      state,
      action: PayloadAction<{ messageId: string; record: ToolCallRecord }>
    ) {
      const { messageId, record } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      const calls = msg.payload?.toolCalls
      if (!calls) {
        if (!msg.payload) {
          msg.payload = { action: 'final_proposal', toolCalls: [record] }
        } else {
          msg.payload.toolCalls = [record]
        }
        return
      }
      const idx = record.id ? calls.findIndex((c) => c.id === record.id) : -1
      if (idx >= 0) {
        calls[idx] = { ...calls[idx], ...record }
      } else {
        calls.push(record)
      }
    },

    /**
     * progress kind=final 提前到达 · 把 card 渲染出来但保持 streaming 状态
     *
     * 后端先发 progress kind=final（含完整 final.raw），再发 event:done。
     * 收到 final partial 时立刻把 card 挂到 message.payload，让 FolderPlanPreview /
     * ChoiceOptionsCard 等卡片渲染；status 保持 streaming，等 done 事件再切到 done。
     *
     * 守卫：仅 streaming 状态的 assistant 消息生效；payload 不存在则补一个空壳
     * （tool_start 也是这样做的，保持一致）。
     */
    assistantPartialFinal(
      state,
      action: PayloadAction<{
        messageId: string
        action: AssistantAction
        card: AssistantCard | undefined
        classifyState?: string
      }>
    ) {
      const { messageId, action: act, card, classifyState } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      if (msg.status !== 'streaming') return
      if (!msg.payload) {
        msg.payload = { action: act, toolCalls: [] }
      } else {
        msg.payload.action = act
      }
      if (card) msg.payload.card = card
      if (typeof classifyState === 'string') {
        msg.payload.classifyState = classifyState
      }
    },

    /**
     * SSE error 事件 · 落 errorMessage 并把 status 转 error
     */
    assistantErrored(
      state,
      action: PayloadAction<{ messageId: string; error: string }>
    ) {
      const { messageId, error } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      msg.errorMessage = error
      msg.status = 'error'
    },

    /**
     * 截断 · 移除指定 messageId 及其之后所有消息
     *
     * 场景：用户气泡的"刷新"按钮 · 点击后丢弃这一轮（user + 紧随的 ai 回复 +
     * 后续可能的全部消息），调用方再 sendUserMessage 同样文案触发新一轮
     *
     * 找不到 messageId（已被其他来源清掉）时静默返回。
     */
    messagesTruncatedFrom(state, action: PayloadAction<{ messageId: string }>) {
      const { messageId } = action.payload
      const ids = state.ids as string[]
      const idx = ids.indexOf(messageId)
      if (idx < 0) return
      const idsToRemove = ids.slice(idx)
      adapter.removeMany(state, idsToRemove)
    },
    /*
     * 从 history 轮询补水 InProgress assistant 消息（豆包式刷新接上）。
     *
     * 场景：用户和 agent 对话中刷新页面 → SSE 断 → agent 后台仍在跑（detached
     * ctx）→ 入口 INSERT 的 InProgress 消息持续被节流 UPDATE assistant_response。
     * 前端启动 useInProgressReconnect 每 1.5s 拉一次 history，拿到该消息的最新
     * 内容，调本 reducer 把已有 hist-asst-* 消息整体覆盖（text/payload/status）。
     *
     * 用整体覆盖（不是增量追加）：每次轮询拿到的是累积 snapshot，幂等覆盖
     * 最简单且不会重复。
     */
    assistantHydratedFromHistory(
      state,
      action: PayloadAction<{
        messageId: string
        text: string
        payload?: AssistantPayload
        status: 'streaming' | 'done' | 'error'
        errorMessage?: string
      }>
    ) {
      const { messageId, text, payload, status, errorMessage } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      msg.text = text
      msg.status = status
      if (payload) msg.payload = payload
      if (errorMessage !== undefined) msg.errorMessage = errorMessage
    },

    /**
     * SSE dimension_updated 时把当前维度链摘要快照到 streaming assistant message
     *
     * live 路径专用 · 让每条 message 持有"该轮 refine 的方案快照", 取代之前
     * "selection 单例 + anchor 选最后一条" 的设计 → live / history 同构。
     *
     * 只写浅字段 (sessionId / chainVersion / layersSummary / expandedSize /
     * layerCount); 深字段 (folders / excludedKeys / taskCreated) 留给 SSE
     * done 后用 backendMessageId 调接口 5 的 hydrate 路径补齐, 跟 history
     * 完全同构。
     *
     * 守卫: 找不到对应 message / 非 assistant / 非 streaming 时静默忽略
     * (防止迟到的事件污染已完成 message)。
     */
    assistantPlanSnapshotted(
      state,
      action: PayloadAction<{
        messageId: string
        sessionId: string
        chainVersion: number
        layersSummary: LayerSummary[]
        expandedSize: number
        layerCount: number
      }>
    ) {
      const {
        messageId,
        sessionId,
        chainVersion,
        layersSummary,
        expandedSize,
        layerCount
      } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      if (msg.status !== 'streaming') return
      msg.payload = {
        ...(msg.payload ?? {}),
        action: 'final_proposal',
        card: {
          kind: 'folder_plan',
          sessionId,
          chainVersion,
          layersSummary,
          expandedSize,
          layerCount
          // folders / excludedKeys / taskCreated 留给 hydrate 路径补齐
        }
      }
    },

    /**
     * 文件夹方案卡 hydrate · 用接口 5 拿到 folders + excludedKeys + taskCreated 后回填
     *
     * 触发链路 (两路同构):
     *   · history: restoreLastConversation 同步 dispatch 所有 folder 消息后,
     *     并发跑 fetchAiChatHistoryFolders → 本 action 回填
     *   · live: SSE done 后, 假定 backend 同步落库, 用 backendMessageId 调
     *     fetchAiChatHistoryFolders → 本 action 回填
     *
     * 只动 payload.card.folders / excludedKeys / taskCreated 三个字段, 不改
     * text / status / 浅字段 (sessionId/chainVersion/layersSummary 等)。
     * kind !== 'folder_plan' 时直接忽略 (协议升级期 / 调用方传错 id 防御)。
     */
    assistantFoldersHydrated(
      state,
      action: PayloadAction<{
        messageId: string
        folders: PlanFolder[]
        excludedKeys?: string[]
        taskCreated?: boolean
      }>
    ) {
      const { messageId, folders, excludedKeys, taskCreated } = action.payload
      const msg = state.entities[messageId]
      if (!msg || msg.kind !== 'assistant') return
      if (msg.payload?.card?.kind !== 'folder_plan') return
      msg.payload.card.folders = folders
      if (excludedKeys !== undefined) {
        msg.payload.card.excludedKeys = excludedKeys
      }
      if (taskCreated !== undefined) {
        msg.payload.card.taskCreated = taskCreated
      }
    },

    /** 清空全部消息 */
    clear: () => adapter.getInitialState()
  },
  extraReducers: (builder) => {
    // 切到新对话 / 切到历史会话 / 切团队 三种场景都要把当前消息清空, 避免陈旧气泡残留
    builder
      .addCase(conversationCleared, () => adapter.getInitialState())
      .addCase(teamSwitched, () => adapter.getInitialState())
  }
})

export const {
  messageAppended,
  assistantDone,
  assistantErrored,
  assistantPartialFinal,
  assistantToolStarted,
  assistantToolEnded,
  assistantHydratedFromHistory,
  assistantPlanSnapshotted,
  assistantFoldersHydrated,
  messageFeedbackSet,
  messagesTruncatedFrom,
  clear
} = messagesSlice.actions

export default messagesSlice.reducer

// ============================================================
// 工具函数 · 构造一条 build_task_status 类型 assistant message
// ============================================================

/**
 * 构造一条独立成 message entity 的"建夹任务状态消息"。
 *
 * 调用方拿到返回值后 dispatch(messageAppended(msg)) 即可让它按时序排到 messages 末尾。
 * card 仅携带 taskId — 任务的运行状态 / 实际建夹数由 BuildTaskStatusMessage
 * 组件内部 useBuildTaskDetail(taskId) 按需调 /ai/chat/task 拉详情渲染, 不在
 * 写入时预测、也不进 Redux 中转。
 *
 * message.id 规则: `build-task-status-${taskId}` · 保证同一 taskId 唯一 (避免
 * 重复 push 导致两条同任务消息)。
 *
 * @param taskId - 后端 build_task.id
 * @returns 完整 AssistantMessage entity, 可直接 dispatch messageAppended
 */
export function makeBuildTaskStatusMessage(taskId: string): AssistantMessage {
  return {
    id: `build-task-status-${taskId}`,
    kind: 'assistant',
    role: 'ai',
    status: 'done',
    text: '',
    createdAt: Date.now(),
    payload: {
      action: 'build_task_status',
      card: {
        kind: 'build_task_status',
        taskId
      }
    }
  }
}
